import React from 'react';
import { Timeline, Tag, Card, Typography, Empty } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  TruckOutlined,
  HomeOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDate, getStatusColor, getStatusText } from '../../query/utils/orderUtils';

const { Text } = Typography;

export interface StatusHistoryItem {
  /** 历史记录ID */
  history_id?: string;
  /** 订单ID */
  order_id: string;
  /** 旧状态 */
  old_status?: string;
  /** 新状态 */
  new_status: string;
  /** 变更者 */
  changed_by: string;
  /** 备注 */
  notes?: string;
  /** 创建时间 */
  created_at: string;
}

export interface StatusHistoryTimelineProps {
  /** 状态历史记录 */
  statusHistory: StatusHistoryItem[];
  /** 当前状态 */
  currentStatus: string;
  /** 是否显示完整历史 */
  showFullHistory?: boolean;
  /** 最多显示条数 */
  maxItems?: number;
  /** 是否正在加载 */
  loading?: boolean;
}

const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({
  statusHistory,
  currentStatus,
  showFullHistory = true,
  maxItems = 10,
  loading = false
}) => {
  const { t } = useTranslation(['modules', 'common']);

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    const statusIcons: Record<string, React.ReactNode> = {
      pending: <ClockCircleOutlined style={{ color: '#faad14' }} />,
      processing: <SyncOutlined spin style={{ color: '#1890ff' }} />,
      shipped: <TruckOutlined style={{ color: '#722ed1' }} />,
      delivered: <HomeOutlined style={{ color: '#52c41a' }} />,
      cancelled: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      refunded: <CloseCircleOutlined style={{ color: '#f5222d' }} />,
      on_hold: <ExclamationCircleOutlined style={{ color: '#d4b106' }} />,
      completed: <CheckCircleOutlined style={{ color: '#52c41a' }} />
    };

    return statusIcons[status.toLowerCase()] || <ClockCircleOutlined />;
  };

  // 格式化状态变更描述
  const formatStatusChange = (oldStatus: string | undefined, newStatus: string) => {
    if (!oldStatus) {
      return t('modules:order.detail.status.created', '订单创建');
    }

    const oldStatusText = getStatusText(oldStatus);
    const newStatusText = getStatusText(newStatus);

    return t('modules:order.detail.status.changed', {
      old: oldStatusText,
      new: newStatusText
    });
  };

  // 处理历史记录数据
  const processedHistory = [...statusHistory]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, showFullHistory ? undefined : maxItems);

  // 如果没有历史记录，显示空状态
  if (processedHistory.length === 0 && !loading) {
    return (
      <Card>
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('modules:order.detail.status.title', '状态历史')}
        </Title>
        <Empty
          description={t('modules:order.detail.status.noHistory', '暂无状态历史记录')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  // 获取当前状态信息
  const currentStatusInfo = {
    status: currentStatus,
    text: getStatusText(currentStatus),
    color: getStatusColor(currentStatus),
    icon: getStatusIcon(currentStatus)
  };

  return (
    <Card className="status-history-timeline" loading={loading}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '16px' }}>
            {t('modules:order.detail.status.title', '状态历史')}
          </Text>
          <Tag
            icon={currentStatusInfo.icon}
            color={currentStatusInfo.color}
            style={{ margin: 0 }}
          >
            {currentStatusInfo.text}
          </Tag>
        </div>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('modules:order.detail.status.current', '当前状态')}
        </Text>
      </div>

      <Timeline
        mode="left"
        items={processedHistory.map((history, index) => {
          const isLatest = index === 0;
          const color = getStatusColor(history.new_status);

          return {
            color,
            dot: getStatusIcon(history.new_status),
            children: (
              <div style={{ paddingLeft: 8 }}>
                {/* 状态变更标题 */}
                <div style={{ marginBottom: 4 }}>
                  <Text strong style={{ fontSize: '14px' }}>
                    {formatStatusChange(history.old_status, history.new_status)}
                  </Text>
                  {isLatest && (
                    <Tag color="blue" style={{ marginLeft: 8, fontSize: '12px' }}>
                      {t('modules:order.detail.status.latest', '最新')}
                    </Tag>
                  )}
                </div>

                {/* 变更者和时间 */}
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t('modules:order.detail.status.by', '由')}{' '}
                    <Text strong style={{ fontSize: '12px' }}>
                      {history.changed_by}
                    </Text>{' '}
                    {t('modules:order.detail.status.at', '于')}{' '}
                    {formatDate(history.created_at, 'long')}
                  </Text>
                </div>

                {/* 备注 */}
                {history.notes && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    backgroundColor: '#f6f6f6',
                    borderRadius: 4,
                    borderLeft: `3px solid ${color}`
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {t('modules:order.detail.status.notes', '备注')}: {history.notes}
                    </Text>
                  </div>
                )}

                {/* 状态变化详情 */}
                {history.old_status && (
                  <div style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px'
                  }}>
                    <Tag
                      color={getStatusColor(history.old_status)}
                      style={{ margin: 0, fontSize: '12px', padding: '0 4px' }}
                    >
                      {getStatusText(history.old_status)}
                    </Tag>
                    <Text type="secondary" style={{ margin: '0 8px' }}>
                      →
                    </Text>
                    <Tag
                      color={color}
                      style={{ margin: 0, fontSize: '12px', padding: '0 4px' }}
                    >
                      {getStatusText(history.new_status)}
                    </Tag>
                  </div>
                )}
              </div>
            )
          };
        })}
      />

      {/* 显示更多提示 */}
      {!showFullHistory && statusHistory.length > maxItems && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">
            {t('modules:order.detail.status.moreItems', '还有 {count} 条历史记录', {
              count: statusHistory.length - maxItems
            })}
          </Text>
        </div>
      )}

      {/* 统计信息 */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('modules:order.detail.status.totalChanges', '状态变更次数')}:
            </Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {statusHistory.length}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('modules:order.detail.status.firstChange', '首次变更')}:
            </Text>
            <div style={{ fontSize: '12px' }}>
              {statusHistory.length > 0
                ? formatDate(statusHistory[statusHistory.length - 1].created_at, 'short')
                : '-'}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('modules:order.detail.status.lastChange', '最近变更')}:
            </Text>
            <div style={{ fontSize: '12px' }}>
              {statusHistory.length > 0
                ? formatDate(statusHistory[0].created_at, 'short')
                : '-'}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// 添加缺失的Title组件
const Title = Typography.Title;

export default StatusHistoryTimeline;